import { COOKIE_DO_CONSENTIMENTO, DIAS_DO_CONSENTIMENTO, type DadosDoMeta, type NomeDoEventoMeta } from "@/lib/camp/meta";

/**
 * O pixel no navegador (brief de 30/10/2026). Sem Advanced Matching: casar
 * pessoa é trabalho do servidor, e aqui nenhum dado de formulário sai.
 *
 * 🔴 Duas formas de mandar, porque o `fbevents.js` manda a URL inteira:
 * - `fbq`, na landing e na inscrição;
 * - imagem com URL limpa, onde o endereço carrega token que abre a inscrição
 *   da família (`/confirmacao/<token>`, `?reserva=<token>`). Sem isso a Meta
 *   receberia o link que mostra o nome da criança.
 *
 * Falhar aqui nunca trava a inscrição.
 */

type Fbq = ((...args: unknown[]) => void) & { callMethod?: (...args: unknown[]) => void; queue: unknown[]; loaded?: boolean; version?: string; push?: unknown };

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

let idDoPixel: string | null = null;
let caminhoLimpo: string | null = null;
let codigoDeTeste: string | null = null;

export function escolhaDosCookies(): "sim" | "nao" | null {
  try {
    const v = document.cookie.match(new RegExp(`(?:^|;\\s*)${COOKIE_DO_CONSENTIMENTO}=(sim|nao)`))?.[1];
    return v === "sim" || v === "nao" ? v : null;
  } catch {
    return null;
  }
}

export function guardarEscolhaDosCookies(escolha: "sim" | "nao"): void {
  document.cookie = `${COOKIE_DO_CONSENTIMENTO}=${escolha}; path=/; max-age=${DIAS_DO_CONSENTIMENTO * 86_400}; samesite=lax; secure`;
}

/** `caminho`: a URL sem o token, nas telas que não podem usar o `fbq`. */
export function configurarPixel(id: string, caminho: string | null, teste: string | null): void {
  idDoPixel = id;
  caminhoLimpo = caminho;
  codigoDeTeste = teste;
}

export function carregarPixel(id: string): void {
  if (window.fbq) return;
  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  } as Fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  window.fbq = fbq;
  window._fbq = fbq;
  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);
  // 🔴 Antes do `init`. Ligado, o pixel lê os campos do formulário sozinho
  // (Automatic Advanced Matching) e o texto dos botões.
  fbq("set", "autoConfig", false, id);
  fbq("init", id);
}

function cookieDoNavegador(nome: string): string | null {
  return document.cookie.match(new RegExp(`(?:^|;\\s*)${nome}=([^;]+)`))?.[1] ?? null;
}

function porImagem(nome: string, eventId: string | null, dados?: Record<string, string | number>): void {
  if (!idDoPixel || !caminhoLimpo) return;
  const q = new URLSearchParams({ id: idDoPixel, ev: nome, dl: `${window.location.origin}${caminhoLimpo}`, noscript: "1" });
  if (eventId) q.set("eid", eventId);
  if (codigoDeTeste) q.set("test_event_code", codigoDeTeste);
  const fbp = cookieDoNavegador("_fbp");
  if (fbp) q.set("fbp", fbp);
  for (const [k, v] of Object.entries(dados ?? {})) q.set(`cd[${k}]`, String(v));
  new Image().src = `https://www.facebook.com/tr?${q.toString()}`;
}

/** PageView e ViewContent. Sem `event_id`: esses dois não têm cópia no servidor. */
export function medirPaginaNoMeta(nome: "PageView" | "ViewContent", dados?: { content_ids: string[]; content_type: string }): void {
  try {
    if (caminhoLimpo) {
      porImagem(nome, null, dados ? { content_ids: JSON.stringify(dados.content_ids), content_type: dados.content_type } : undefined);
      return;
    }
    window.fbq?.("track", nome, dados);
  } catch {
    // medição nunca derruba a inscrição
  }
}

/** InitiateCheckout, Lead e Purchase, com o `event_id` que o servidor usou. */
export function medirNoMeta(nome: NomeDoEventoMeta, d: DadosDoMeta | null | undefined): void {
  if (!d) return;
  try {
    if (escolhaDosCookies() !== "sim") return;
    const dados = { value: d.value, currency: "BRL", content_ids: d.contentIds, content_type: "product", num_items: d.numItems };
    if (caminhoLimpo) {
      porImagem(nome, d.eventId, { ...dados, content_ids: JSON.stringify(d.contentIds) });
      return;
    }
    window.fbq?.("track", nome, dados, { eventID: d.eventId });
  } catch {
    // medição nunca derruba a inscrição
  }
}
